import {bignumber, floor, round, unit} from "mathjs";
import {KeyOfType} from "../types/types";
import {AsYouType, type CountryCode, isValidPhoneNumber, parsePhoneNumberFromString,} from 'libphonenumber-js';

/**
 * Return range of number between start and end.
 */
export const range = (start: number, end: number): number[] => {
  const result: number[] = [];
  for (let i = start; i < end; i++) {
    result.push(i);
  }
  return result;
};

/**
 * Converts internal unit of measure designations to js unit of measure
 * designations.
 *
 * @param uom the unit of measure
 */
const toMathJsUom = (uom: string): string | undefined => {
  switch (uom) {
    case "barrels":
      return "obl";
    case "gallons":
      return "gal";
    case "feet":
      return "ft";
    case "inches":
      return "in";
    default:
      return undefined;
  }
};

/**
 * Returns the volume converted to the specified unit of measure. Returns undefined
 * if a math.js unit of measure couldn't be found.
 *
 * @param fromUom the from unit of measure
 * @param toUom the to unit of measure
 * @param quantity the quantity to convert
 */
export const convertVolumeQuantity = (
  fromUom: string | undefined,
  toUom: string | undefined,
  quantity: number | undefined
): number | undefined => {
  if (
    fromUom === undefined ||
    toUom === undefined ||
    quantity === undefined ||
    fromUom === toUom
  ) {
    return quantity;
  }

  const fromUomMathJs = toMathJsUom(fromUom);
  const toUomMathJs = toMathJsUom(toUom);

  if (fromUomMathJs == null || toUomMathJs == null) {
    return undefined;
  }

  const fromQty = unit(quantity, fromUomMathJs);
  return fromQty.toNumber(toUomMathJs);
};

/**
 * Returns the volume converted to the specified unit of measure. Returns undefined
 * if a math.js unit of measure couldn't be found.
 *
 * @param fromUom the from unit of measure
 * @param toUom the to unit of measure
 * @param quantity the quantity to convert
 */
export const convertLinearQuantity = (
  fromUom: string | undefined,
  toUom: string | undefined,
  quantity: number | undefined
): number | undefined => {
  if (
    fromUom === undefined ||
    toUom === undefined ||
    quantity === undefined ||
    fromUom === toUom
  ) {
    return quantity;
  }

  const fromUomMathJs = toMathJsUom(fromUom);
  const toUomMathJs = toMathJsUom(toUom);

  if (fromUomMathJs == null || toUomMathJs == null) {
    return undefined;
  }

  const fromQty = unit(quantity, fromUomMathJs);
  return fromQty.toNumber(toUomMathJs);
};

export class FeetInches {
  feet: number;
  inches: number;
  quarters: number;

  constructor(feet: number | undefined, inches: number | undefined, quarters: number | undefined) {
    this.feet = feet ?? 0
    this.inches = inches ?? 0
    this.quarters = quarters ?? 0
  }

  asInches(): number {
    return inches(this.feet, this.inches, this.quarters);
  }

  toString(): string {
    return `${this.feet.toFixed(0)}' ${this.inches.toFixed(0)}" ${this.quarters.toFixed(0)}/4`
  }

  static fromInches(value: number | undefined): FeetInches {
    value = value ?? 0
    let feet = Math.floor(value / 12);
    let inches = Math.floor(value - feet * 12);
    if (inches >= 12) {
      feet++;
      inches = 0;
    }

    const decimalPart = Number((value % 1).toFixed(2));
    let quarters = 0;
    if (decimalPart <= 0.35 && decimalPart > 0.1) {
      quarters = 1;
    } else if (decimalPart <= 0.65 && decimalPart > 0.35) {
      quarters = 2;
    } else if (decimalPart <= 0.85 && decimalPart > 0.65) {
      quarters = 3;
    }

    return new FeetInches(feet, inches, quarters)
  }

  static fromFeet(value: number | undefined): FeetInches {
    const inches = (value ?? 0) * 12;
    return this.fromInches(inches)
  }
}

export const inchesToFeet = (value: number | undefined): FeetInches => {
  return FeetInches.fromInches(value)
};

export const inches = (feet: number | undefined, inches: number | undefined, quarters: number | undefined) => {
  let total = 0

  const feetValue = feet ?? 0
  const inchesValue = inches ?? 0
  const quartersValue = quarters ?? 0

  if (typeof inches !== "undefined") {
    total = inchesValue
  }

  if (feetValue > 0) {
    const quarterInches = floor(bignumber((quartersValue / 4)))
    const additionalInches = round(bignumber(feetValue).times(bignumber("12")), 3)
    total = bignumber(inches).add(additionalInches).add(quarterInches).toNumber()
  }

  total = round(total, 3)

  return total
}

export const compareString = (
  a: string | undefined,
  b: string | undefined
): number => {
  if (a === b) {
    return 0;
  }

  if (a === undefined) {
    return 1;
  }

  if (b === undefined) {
    return -1;
  }

  return a.toLocaleLowerCase().localeCompare(b.toLocaleLowerCase());
};

export const compareNumber = (
  a: number | undefined,
  b: number | undefined
): number => {
  if (a === b) {
    return 0;
  }

  if (a === undefined) {
    return 1;
  }

  if (b === undefined) {
    return -1;
  }

  return a - b;
};

export function getStringSorter<T>(
  stringField: KeyOfType<T, string | undefined>
) {
  return (a: T, b: T) => {
    const aString = a[stringField] as unknown as string;
    const bString = b[stringField] as unknown as string;

    if (aString === bString) {
      return 0;
    }

    if (aString === undefined) {
      return 1;
    }

    if (bString === undefined) {
      return -1;
    }

    return aString.toLocaleLowerCase().localeCompare(bString.toLocaleLowerCase());
  };
}

export function getNumberSorter<T>(
  numberField: KeyOfType<T, number | undefined>,
  undefinedSort: "top" | "bottom" = "bottom",
) {
  return (a: T, b: T) => {
    const aNumber = a[numberField] as unknown as number;
    const bNumber = b[numberField] as unknown as number;
    const undefinedPos = undefinedSort === "bottom" ? -1 : 1;

    if (aNumber === bNumber) {
      return 0;
    }

    if (aNumber === undefined) {
      return undefinedPos;
    }

    if (bNumber === undefined) {
      return -undefinedPos;
    }

    return aNumber - bNumber;
  };
}

export function getBooleanSorter<T>(
  booleanField: KeyOfType<T, boolean | undefined>
) {
  return (a: T, b: T) => {
    const aBoolean = a[booleanField] as unknown as number;
    const bBoolean = b[booleanField] as unknown as number;

    if (aBoolean === bBoolean) {
      return 0;
    }

    if (aBoolean === undefined) {
      return 1;
    }

    if (bBoolean === undefined) {
      return -1;
    }

    return aBoolean ? -1 : 1;
  };
}

export function emptyOrUndefined<T>(arr: Array<T> | undefined): boolean {
  return typeof arr === 'undefined' || arr.length === 0
}

export function firstOrUndefined<T>(arr: Array<T> | undefined): T | undefined {
  return atIndexOrUndefined(arr, 0)
}

export function lastOrUndefined<T>(arr: Array<T> | undefined): T | undefined {
  if (typeof arr === 'undefined') {
    return undefined
  }

  const index = arr.length - 1

  return atIndexOrUndefined(arr, index)
}

export function atIndexOrUndefined<T>(arr: Array<T> | undefined, index: number): T | undefined {
  let value: T | undefined = undefined;

  if (arr && arr.length > index) {
    value = arr[index];
  }

  return value;
}

export function containsValue<T>(arr: Array<T> | undefined, lookValue: T): boolean {
  let contains = false;

  if (arr) {
    contains = arr.findIndex((value) => value === lookValue) !== -1;
  }

  return contains;
}

export function contains<T>(arr: Array<T> | undefined, lookup: (value: T) => boolean): boolean {
  let contains = false;

  if (arr) {
    contains = arr.findIndex((value) => lookup(value)) !== -1;
  }

  return contains;
}

export function removeUndefined(obj: Record<string, any>): Record<string, any> {
  Object.keys(obj).forEach((key) => obj[key] == null && delete obj[key]);
  return obj;
}

export function indexesOf<T>(arr: Array<T> | undefined, lookup: (value: T) => boolean): number[] | undefined {
  let indexes: number[] | undefined = undefined;

  if (arr) {
    indexes = []
    arr.forEach((value, index) => {
      if (lookup(value)) {
        indexes?.push(index)
      }
    })
  }

  return indexes;
}

export function sortMap<T>(map: Map<string, T>): Map<string, T> {
  const sortedKeys = Array.from(map.keys()).sort();
  const sortedMap = new Map<string, T>();
  sortedKeys.forEach((key) => {
    const value = map.get(key);
    if (value) {
      sortedMap.set(key, value);
    }
  });
  return sortedMap;
}

export function getValueByKey<K extends keyof any, V>(record: Record<K, V>, key: K): V | undefined {
  if (key in record) {
    return record[key] as V | undefined
  } else {
    return undefined
  }
}

export function inCircle(a: number, b: number, x: number, y: number, r: number) {
  const dist_points = (a - x) * (a - x) + (b - y) * (b - y);
  r *= r;
  return dist_points < r;
}

export function sum<W>(values: W[] | undefined, mapper: (value: W) => number | undefined): number {
  let sum = 0

  if (values) {
    for (const value of values) {
      const v = mapper(value)
      if (typeof v !== 'undefined') {
        sum += v
      }
    }
  }

  return sum
}

export function isActive(dttm: number, startDttm: number | undefined, endDttm: number | undefined): boolean {
  if (typeof startDttm === 'undefined' && typeof endDttm === 'undefined') {
    return true
  }

  if (startDttm && startDttm > dttm) {
    return false
  }

  if (endDttm && endDttm < dttm) {
    return false
  }

  return true
}

export const isEmptyOrNullOrUndefined = (str: string | null | undefined): str is "" | null | undefined => {
  return str === null || str === undefined || str === "";
}

export const validateEmail = (v?: string) => {
  const s = (v ?? '').trim();
  if (!s) {
    return undefined
  }

  const re = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
  return re.test(s) ? null : 'Enter a valid email';
};

type PhoneOpts = { region: string; allowInternational?: boolean };

const parseWithFallback = (raw: string, region?: string, allowIntl?: boolean) => {
  const reg = (region?.toUpperCase() as CountryCode) || undefined;
  // Try with region first
  let n = parsePhoneNumberFromString(raw, reg);
  if ((!n || !n.isValid()) && allowIntl) {
    // Try parsing without region (handles +XX numbers)
    n = parsePhoneNumberFromString(raw);
  }
  return n && n.isValid() ? n : undefined;
};

export const validatePhone = (v?: string, opts?: PhoneOpts) => {
  const s = (v ?? '').trim();
  if (!s) {
    return undefined
  }

  const region = (opts?.region ?? 'US').toUpperCase();
  const allowIntl = opts?.allowInternational !== false;

  // Fast path: built-in validator (region-aware if provided)
  const quickValid =
    allowIntl ? isValidPhoneNumber(s) : isValidPhoneNumber(s, region as CountryCode);

  if (!quickValid) {
    // Try robust parse fallback before failing
    const parsed = parseWithFallback(s, region, allowIntl);
    if (!parsed) return 'Enter a valid phone number';
    if (!allowIntl && parsed.country !== (region as CountryCode)) {
      return `Enter a valid ${region} number`;
    }
    return null;
  }

  if (!allowIntl) {
    const parsed = parseWithFallback(s, region, allowIntl);
    if (!parsed) return 'Enter a valid phone number';
    if (parsed.country !== (region as CountryCode)) return `Enter a valid ${region} number`;
  }

  return null;
};

/** Pretty display: NATIONAL if matches region, else INTERNATIONAL. */
export const formatPhoneForDisplay = (v: string, opts?: PhoneOpts) => {
  const s = (v ?? '').trim();
  if (!s) return s;
  const region = (opts?.region ?? 'US').toUpperCase();
  const allowIntl = opts?.allowInternational !== false;

  const parsed = parseWithFallback(s, region, allowIntl);
  if (!parsed) return v;

  return parsed.country === (region as CountryCode)
    ? parsed.formatNational()
    : parsed.formatInternational();
};

/** Normalize for storage/transmission (E.164) or return null if invalid. */
export const toE164 = (v?: string, region = 'US') => {
  const s = (v ?? '').trim();
  if (!s) return null;
  const parsed = parseWithFallback(s, region, true);
  return parsed ? parsed.format('E.164') : null;
};

/** Optional helper: format as you type (use in onChange). */
export const asYouType = (raw: string, region = 'US') => {
  const typer = new AsYouType(region as CountryCode);
  return typer.input(raw);
};

export const pascal = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
